/**
 * RH-02 Rhythm autocorrelation (INV-CH-RH-02, ENGINE-SPEC §3.5, §4; changes C-RH-POOL, C-RH-DAMP).
 *
 * MASD/mean of sentence length (mean absolute successive difference over the mean), pooled within
 * the joint segments: (Σ_s D_s / Σ_s c_s) / pooled mean, where D_s is the sum of |x_i − x_{i−1}|
 * over adjacent prose sentences inside segment s and c_s their number (differences never span a
 * segment boundary). Run-on units are excluded. The interval is a stratified paragraph-block
 * bootstrap; with fewer than 8 blocks an analytic interval (RH-01's CV standard error × 1.5, a
 * conservative widening because successive differences are a less efficient dispersion estimate).
 */
import { derivePrior } from './framework.ts';
import {
	type DispItems,
	pooledMasd,
	rhythmSentenceLengths,
	runDispersion,
	sentenceItems,
} from './helpers.ts';
import type { ChannelDef, ChannelImpl, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0.15, 88],
	[0.25, 72],
	[0.35, 55],
	[0.5, 36],
	[0.7, 18],
];

export const rh02Def: ChannelDef = {
	id: 'RH-02',
	name: 'Rhythm autocorrelation',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'distributional',
	features: [{ id: 'masd', label: 'Pooled within-segment MASD / mean sentence length' }],
	minExposure: 6,
	exposureUnit: 'sentences',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'MASD / mean sentence length',
	prior: derivePrior(RAMP, 1, 'sentences', 1.2),
	weight: 1.0,
	nd: 1,
	changes: ['C-RH-POOL', 'C-RH-DAMP'],
};

function run(ctx: RunContext) {
	const { len, para } = rhythmSentenceLengths(ctx.doc);
	let d = 0;
	let s1 = 0;
	for (let i = 0; i < len.length; i++) {
		s1 += len[i] as number;
		if (i > 0) d += Math.abs((len[i] as number) - (len[i - 1] as number));
	}
	const N = len.length;
	const meanAll = N ? s1 / N : 0;
	const masdWhole = N > 1 && meanAll > 0 ? d / (N - 1) / meanAll : 0;
	return runDispersion(ctx, {
		def: rh02Def,
		salt: 0x52480002,
		analyticFactor: 1.5,
		build: (segOf): { items: DispItems; exposure: number } => ({
			items: sentenceItems(len, para, segOf),
			exposure: N,
		}),
		stat: (a) => {
			const m = pooledMasd(a);
			return { value: m.value, df: m.diffs, N: m.N, mean: m.mean };
		},
		extra: () => ({ features: { masdWhole, meanSentenceLength: meanAll }, summary: [] }),
	});
}

export const rh02: ChannelImpl = { def: rh02Def, phase: 'derived', run };
